/* eslint-disable @typescript-eslint/no-explicit-any */
import { inject, Injectable, Injector } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';

import { environment } from '@env/environment';

import { BehaviorSubject, filter, mergeMap, Observable, take } from 'rxjs';

import { AuthService } from '../services/auth.service';
import { LoggerService } from '../services/logger.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private injector = inject(Injector);
  private logger = inject(LoggerService);

  private secureRoutes = [
    environment.services.assetApi,
    environment.services.catalogApi,
    environment.services.idpApi,
    environment.services.notificationsApi,
    environment.services.notificationsHub,
    environment.services.oneHubApi,
    environment.services.portalApi,
    environment.services.referenceApi,
    environment.services.autocareApi,
    environment.services.searchApi,
    environment.services.autocareReferenceApi,
  ];

  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<boolean | null>(null);

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.secureRoutes.some((route) => req.url.startsWith(route))) {
      return next.handle(req);
    }

    if (req.url.includes('hub/oauth2')) {
      return next.handle(req);
    }

    // Lazy inject to avoid circular dependency (HTTP_INTERCEPTORS → AuthService → HttpClient → HTTP_INTERCEPTORS)
    const authService = this.injector.get(AuthService);
    const accessToken = authService.getAccessToken();

    // Another refresh is already in progress — wait for it
    if (this.isRefreshing) {
      return this.waitForRefresh(authService, req, next);
    }

    // Token is missing or expired — trigger a refresh
    if (!accessToken || !authService.isAuthenticated) {
      return this.refreshAndProceed(authService, req, next);
    }

    // Token is valid — attach and proceed
    return next.handle(this.addToken(req, accessToken));
  }

  //#region Helpers

  private addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
    });
  }

  private refreshAndProceed(authService: AuthService, req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    this.logger.log('✅ Forcing refresh token', req.url);

    return authService.refreshToken().pipe(
      mergeMap(() => {
        this.logger.log('✅ Finished refresh token', req.url);

        this.isRefreshing = false;
        this.refreshTokenSubject.next(true);

        return next.handle(this.addToken(req, authService.getAccessToken()));
      }),
    );
  }

  private waitForRefresh(authService: AuthService, req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    this.logger.log('⏳ Refreshing token, waiting for new token to be available...', req.url);

    return this.refreshTokenSubject.pipe(
      filter((value) => value !== null),
      take(1),
      mergeMap(() => {
        this.logger.log('✅ Refreshed token...', req.url);
        return next.handle(this.addToken(req, authService.getAccessToken()));
      }),
    );
  }

  //#endregion
}
